import {
  Badge,
  Button,
  cn,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDateLocal } from "@/shared/lib/date-time";
import { RichTextOrPlain } from "@/shared/ui/RichTextOrPlain";
import type { AssignedTaskDTO } from "../api/client";
import { usePerformanceMeTasksQuery } from "../api/queries";
import { assignedTaskPriorityBadgeClass } from "../lib/assignedTaskStatusStyles";
import {
  labelAssignedTaskAlertStatus,
  labelProjectTaskPriority,
} from "../lib/performanceLaoLabels";
import {
  sortTasksByAlertStatus,
  sortTasksByDueDate,
  sortTasksByPriority,
  sortTasksByUrgency,
} from "../lib/sortAssignedTasks";
import { taskUrgencyForTask } from "../lib/taskUrgency";
import { performanceRoutes } from "../performanceRoutes";
import { AssignedTaskStatusSelect } from "../ui/AssignedTaskStatusSelect";
import { ProjectAssignedTaskProgressField } from "../ui/ProjectAssignedTaskProgressField";

const SOURCE_LABEL: Record<AssignedTaskDTO["source"], string> = {
  meeting: "ກອງປະຊຸມ",
  project: "ໂຄງການ",
};

function taskContextLine(t: AssignedTaskDTO): string {
  if (t.source === "meeting") {
    return `${SOURCE_LABEL.meeting} · ${t.meetingTitle}`;
  }
  return `${SOURCE_LABEL.project} · ${t.projectName}`;
}

type StatusFilter = "all" | "pending" | "in_progress" | "done";
type SourceFilter = "all" | "meeting" | "project";
type SortMode = "priority" | "urgency" | "dueAsc" | "dueDesc" | "alert";

const SORT_OPTIONS: { value: SortMode; label: string }[] = [
  { value: "priority", label: "ຄວາມສຳຄັນ (ສູງ → ຕ່ຳ)" },
  { value: "urgency", label: "ຮີບດ່ວນ (ເກີນກຳນົດກ່ອນ)" },
  { value: "alert", label: "ການແຈ້ງເຕືອນ (ເກີນ/ໃກ້ຄົບກ່ອນ)" },
  { value: "dueAsc", label: "ວັນຄົບ ກ່ອນ → ຫຼັງ" },
  { value: "dueDesc", label: "ວັນຄົບ ຫຼັງ → ກ່ອນ" },
];

function matchesStatusFilter(t: AssignedTaskDTO, f: StatusFilter): boolean {
  if (f === "all") return true;
  if (f === "done") return t.status === "done";
  if (f === "pending") return t.status === "pending";
  if (f === "in_progress")
    return t.source === "project" && t.status === "in_progress";
  return true;
}

function matchesSourceFilter(t: AssignedTaskDTO, f: SourceFilter): boolean {
  if (f === "all") return true;
  return t.source === f;
}

export function PerformanceTasksPage() {
  const navigate = useNavigate();
  const { data, isLoading } = usePerformanceMeTasksQuery();
  const tasks = data?.data ?? [];

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [sortMode, setSortMode] = useState<SortMode>("priority");

  const displayed = useMemo(() => {
    let rows = tasks.filter(
      (t) =>
        matchesStatusFilter(t, statusFilter) &&
        matchesSourceFilter(t, sourceFilter),
    );
    if (sortMode === "priority") rows = sortTasksByPriority(rows);
    else if (sortMode === "urgency") rows = sortTasksByUrgency(rows);
    else if (sortMode === "dueAsc") rows = sortTasksByDueDate(rows, "asc");
    else if (sortMode === "dueDesc") rows = sortTasksByDueDate(rows, "desc");
    else rows = sortTasksByAlertStatus(rows);
    return rows;
  }, [tasks, statusFilter, sourceFilter, sortMode]);

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl tracking-tight">
              ວຽກທີ່ໄດ້ຮັບມອບໝາຍ
            </h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ຈາກກອງປະຊຸມ ແລະ ວຽກໃນໂຄງການ — ປ່ຽນສະຖານະຈາກເມນູ, ກອງ/ລຽງຕາມຕ້ອງການ
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            className="shrink-0"
            onClick={() => navigate({ to: performanceRoutes.hub })}
          >
            <ArrowLeft className="size-4" />
            ກັບໄປ KPI ສ່ວນຕົວ
          </Button>
        </div>

        {!isLoading && tasks.length > 0 ? (
          <div className="mb-6 flex flex-col gap-4">
            <div>
              <p className="mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                ສະຖານະ
              </p>
              <Tabs
                value={statusFilter}
                onValueChange={(v) => setStatusFilter(v as StatusFilter)}
              >
                <TabsList className="flex h-auto w-full flex-wrap gap-1">
                  <TabsTrigger value="all">ທັງໝົດ</TabsTrigger>
                  <TabsTrigger value="pending">ລໍຖ້າເຮັດ</TabsTrigger>
                  <TabsTrigger value="in_progress">ກຳລັງເຮັດ</TabsTrigger>
                  <TabsTrigger value="done">ສຳເລັດ</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div>
              <p className="mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                ແຫຼ່ງ
              </p>
              <Tabs
                value={sourceFilter}
                onValueChange={(v) => setSourceFilter(v as SourceFilter)}
              >
                <TabsList className="flex h-auto w-full flex-wrap gap-1">
                  <TabsTrigger value="all">ທັງໝົດ</TabsTrigger>
                  <TabsTrigger value="meeting">ກອງປະຊຸມ</TabsTrigger>
                  <TabsTrigger value="project">ໂຄງການ</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-muted-foreground text-sm">ລຽງ:</span>
              <Select
                value={sortMode}
                onValueChange={(v) => setSortMode(v as SortMode)}
              >
                <SelectTrigger
                  size="sm"
                  className="h-9 w-full min-w-56 max-w-md sm:w-72"
                  aria-label="ລຽງລາຍການ"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : null}

        {isLoading ? (
          <div className="space-y-3">
            <div className="h-20 animate-pulse rounded-xl border bg-muted/40" />
            <div className="h-20 animate-pulse rounded-xl border bg-muted/40" />
            <div className="h-20 animate-pulse rounded-xl border bg-muted/40" />
          </div>
        ) : tasks.length === 0 ? (
          <p className="py-12 text-center text-muted-foreground">
            ບໍ່ມີວຽກມອບໝາຍຈາກກອງປະຊຸມ ຫຼື ໂຄງການ
          </p>
        ) : displayed.length === 0 ? (
          <p className="py-12 text-center text-muted-foreground">
            ບໍ່ມີລາຍການຕາມຕົວກອງທີ່ເລືອກ
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-xl border">
            {displayed.map((t) => {
              const u = taskUrgencyForTask(t);
              const pendingLike = t.status !== "done";
              return (
                <li
                  key={`${t.source}-${t.id}`}
                  className={cn(
                    "flex flex-col gap-2 px-4 py-4 sm:flex-row sm:items-center sm:justify-between",
                    t.alertStatus === "overdue" &&
                      "border-l-4 border-l-destructive bg-destructive/5",
                    t.alertStatus === "due_soon" &&
                      "border-l-4 border-l-amber-600 bg-amber-500/5 dark:border-l-amber-500",
                  )}
                >
                  <div className="min-w-0">
                    <div className="font-medium">
                      <RichTextOrPlain text={t.title} />
                    </div>
                    <p className="text-muted-foreground text-xs">
                      {taskContextLine(t)}
                      {t.dueDate ? ` · ກຳນົດ ${formatDateLocal(t.dueDate)}` : ""}
                    </p>
                    {t.source === "project" ? (
                      <ProjectAssignedTaskProgressField
                        taskId={t.id}
                        progressPercent={t.progressPercent}
                        className="mt-2"
                      />
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <Badge variant="secondary">{SOURCE_LABEL[t.source]}</Badge>
                    {t.source === "project" && t.priority ? (
                      <Badge
                        variant="outline"
                        className={assignedTaskPriorityBadgeClass(
                          t.priority,
                          t.status,
                        )}
                      >
                        ຄວາມສຳຄັນ: {labelProjectTaskPriority(t.priority)}
                      </Badge>
                    ) : null}
                    {pendingLike && u === "overdue" ? (
                      <Badge variant="destructive">
                        {labelAssignedTaskAlertStatus("overdue")}
                      </Badge>
                    ) : null}
                    {pendingLike && u === "due_soon" ? (
                      <Badge
                        variant="outline"
                        className="border-amber-600/60 text-amber-950 dark:border-amber-500/50 dark:text-amber-100"
                      >
                        {labelAssignedTaskAlertStatus("due_soon")}
                      </Badge>
                    ) : null}
                    {t.source === "meeting" ? (
                      <AssignedTaskStatusSelect
                        variant="meeting"
                        id={t.id}
                        status={t.status}
                      />
                    ) : (
                      <AssignedTaskStatusSelect
                        variant="project"
                        id={t.id}
                        status={t.status}
                      />
                    )}
                    {t.source === "meeting" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          navigate({
                            to: "/app/communication/minutes/$id",
                            params: { id: t.meetingId },
                            search: { from: "minutes" },
                          })
                        }
                      >
                        ເປີດບັນທຶກ
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          navigate({
                            to: "/app/performance/projects/$projectId",
                            params: { projectId: t.projectId },
                          })
                        }
                      >
                        ເປີດໂຄງການ
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Main>
    </>
  );
}
